import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaComment, FaGift } from 'react-icons/fa';
import { FaEllipsis } from 'react-icons/fa6';
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiCalendar,
  FiStar,
  FiBriefcase,
  FiUserPlus,
  FiShare2,
  FiTrash2,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { PersonalConnection } from '../../../../types/misc';
import { connectionService } from '../../../../services';
import { ProfileField, ProfileDataType } from '../../../../types/profile';

import Avatar from '../../../../components/misc/Avatar';
import ConnectionProfileFields from '../../../../components/misc/ConnectionProfileFields';
import IntroductionModal from '../../../../components/modals/IntroductionModal';
import ShareWithConnectionModal from '../../../../components/modals/ShareWithConnectionModal';
import WarningModal from '../../../../components/modals/WarningModal';

interface ConnectionOverviewScreenProps {
  connection: PersonalConnection;
  onDeleted?: (id: number) => void;
}

export default function ConnectionOverviewScreen({
  connection,
  onDeleted,
}: ConnectionOverviewScreenProps): JSX.Element {
  const { t } = useTranslation();

  const [profilePicture, setProfilePicture] = useState<string>();
  const [profileFields, setProfileFields] = useState<ProfileField[]>([]);
  const [loadingFields, setLoadingFields] = useState<boolean>(false);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const typePickerRef = useRef<HTMLDivElement | null>(null);
  const [showOtherPicker, setShowOtherPicker] = useState(false);
  const otherPickerRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showBreakConfirm, setShowBreakConfirm] = useState(false);

  const hasPhone = profileFields.some((f) => f.dataType === ProfileDataType.PHONENUMBER);
  const hasEmail = profileFields.some((f) => f.dataType === ProfileDataType.EMAIL);
  const hasAddress = profileFields.some((f) => f.dataType === ProfileDataType.ADDRESS);
  const hasBday = profileFields.some((f) => f.dataType === ProfileDataType.BIRTHDATE);
  const hasBusiness = profileFields.some((f) => f.dataType === ProfileDataType.BUSINESSNAME);

  useEffect(() => {
    if (!connection?.id) return;

    const userId = connection.id.toString();

    connectionService
      .getProfilePictureOfUser(userId)
      .then((i) => {
        setProfilePicture(i);
      })
      .catch(() => {
        setProfilePicture(undefined);
      });

    setLoadingFields(true);
    connectionService
      .getSharedDataByUser(userId)
      .then((d) => {
        setProfileFields(d);
      })
      .catch(() => {
        setProfileFields([]);
      })
      .finally(() => setLoadingFields(false));
  }, [connection?.id]);

  // handle outside clicks and escape for the types dropdown
  useEffect(() => {
    const handleOutsideClick: EventListener = (e) => {
      const target = e.target as Node | null;
      if (typePickerRef.current && target && !typePickerRef.current.contains(target)) {
        setShowTypePicker(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowTypePicker(false);
      }
    };

    if (showTypePicker) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showTypePicker]);

  // Other dropdown behaviour (outside click + Escape)
  useEffect(() => {
    const handleOutsideClick: EventListener = (e) => {
      const target = e.target as Node | null;
      if (otherPickerRef.current && target && !otherPickerRef.current.contains(target)) {
        setShowOtherPicker(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowOtherPicker(false);
      }
    };

    if (showOtherPicker) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showOtherPicker]);

  const handleRequestData = async (type: ProfileDataType) => {
    try {
      await connectionService.createOrUpdateDataRequest(connection.id.toString(), [type]);
      toast.success(t('toast.success.requestSent'));
      setShowTypePicker(false);
    } catch (err) {
      console.error(err);
      toast.error(t('toast.error.loadFailed'));
    }
  };

  const handleOtherOption = (opt: string) => {
    setShowOtherPicker(false);

    if (opt === 'introduce') {
      setShowIntroModal(true);
      return;
    }

    if (opt === 'share') {
      setShowShareModal(true);
      return;
    }

    if (opt === 'break') {
      setShowBreakConfirm(true);
      return;
    }

    toast.info(t('comingSoon'));
  };

  const handleBreakConnection = async () => {
    setShowBreakConfirm(false);
    try {
      await connectionService.deleteConnection(connection.id);
      if (typeof onDeleted === 'function') onDeleted(connection.id);
      toast.success(t('toast.success.connectionBroken'));
      navigate('/connections');
    } catch (e) {
      console.error('Failed to delete connection', e);
      toast.error(t('toast.error.general'));
    }
  };

  const fullName = `${connection.firstName} ${connection.lastName}`;

  return (
    <div className="h-full w-full overflow-y-auto p-4 sm:p-6">
      <div className="rounded-xl bg-secondary p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col items-center gap-3 sm:gap-4">
          <Avatar src={profilePicture} alias={fullName} className="h-28 w-28 text-2xl sm:h-40 sm:w-40 sm:text-4xl" />
          <p className="text-lg font-bold text-primary-900 sm:text-2xl">{fullName}</p>
        </div>

        {/* Options */}
        <div className="mx-auto mt-6 flex w-full max-w-3xl flex-col items-stretch justify-center gap-3 sm:flex-row sm:flex-wrap sm:items-center md:justify-between">
          <button
            type="button"
            className="flex w-full cursor-not-allowed flex-row items-center justify-center gap-3 rounded-lg bg-details-500 px-4 py-3 text-sm opacity-80 transition-colors sm:w-40 sm:flex-col sm:items-center sm:py-2 sm:text-xs">
            <FaGift className="h-5 w-5 flex-shrink-0 sm:h-6 sm:w-6" aria-hidden />
            <span className="truncate text-center">{t('page.home.connections.sendCard')}</span>
          </button>

          {/* Request Data — show dropdown with same styling as profile picker */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowTypePicker((s) => !s)}
              aria-haspopup="menu"
              aria-expanded={showTypePicker}
              className="flex w-full flex-row items-center justify-center gap-3 rounded-lg bg-secondary-200 px-4 py-3 text-sm text-primary transition-colors sm:w-40 sm:flex-col sm:items-center sm:py-2 sm:text-xs">
              <FaComment className="h-5 w-5 flex-shrink-0 sm:h-6 sm:w-6" aria-hidden />
              <span className="truncate text-center">{t('page.home.connections.requestData')}</span>
            </button>

            {showTypePicker && (
              <div
                ref={typePickerRef}
                className="absolute left-0 right-0 mt-2 w-full rounded-md border bg-secondary p-2 text-primary shadow sm:left-auto sm:right-0 sm:w-56">
                {!hasEmail && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded p-2 text-left text-primary hover:bg-secondary-50"
                    onClick={() => handleRequestData(ProfileDataType.EMAIL)}>
                    <FiMail className="h-4 w-4" />
                    {t('page.profile.emailAddress')}
                  </button>
                )}
                {!hasPhone && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded p-2 text-left text-primary hover:bg-secondary-50"
                    onClick={() => handleRequestData(ProfileDataType.PHONENUMBER)}>
                    <FiPhone className="h-4 w-4" />
                    {t('page.profile.phoneNumber')}
                  </button>
                )}
                {!hasAddress && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded p-2 text-left text-primary hover:bg-secondary-50"
                    onClick={() => handleRequestData(ProfileDataType.ADDRESS)}>
                    <FiMapPin className="h-4 w-4" />
                    {t('page.profile.address')}
                  </button>
                )}
                {!hasBday && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded p-2 text-left text-primary hover:bg-secondary-50"
                    onClick={() => handleRequestData(ProfileDataType.BIRTHDATE)}>
                    <FiCalendar className="h-4 w-4" />
                    {t('page.profile.birthday')}
                  </button>
                )}
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded p-2 text-left text-primary hover:bg-secondary-50"
                  onClick={() => handleRequestData(ProfileDataType.MOMENT)}>
                  <FiStar className="h-4 w-4" />
                  {t('page.profile.specialDates')}
                </button>
                {!hasBusiness && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded p-2 text-left text-primary hover:bg-secondary-50"
                    onClick={() => handleRequestData(ProfileDataType.BUSINESSNAME)}>
                    <FiBriefcase className="h-4 w-4" />
                    {t('page.profile.businessName')}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={() => setShowOtherPicker((s) => !s)}
              aria-haspopup="menu"
              aria-expanded={showOtherPicker}
              className="flex w-full flex-row items-center justify-center gap-3 rounded-lg bg-secondary-200 px-4 py-3 text-sm text-primary transition-colors sm:w-40 sm:flex-col sm:items-center sm:py-2 sm:text-xs">
              <FaEllipsis className="h-5 w-5 flex-shrink-0 sm:h-6 sm:w-6" aria-hidden />
              <span className="truncate text-center">{t('page.home.connections.otherButton')}</span>
            </button>
            {showOtherPicker && (
              <div
                ref={otherPickerRef}
                className="absolute left-0 right-0 mt-2 w-full rounded-md border bg-secondary p-2 text-primary shadow sm:left-auto sm:right-0 sm:w-56">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded p-2 text-left text-primary hover:bg-secondary-50"
                  onClick={() => handleOtherOption('introduce')}>
                  <FiUserPlus className="h-4 w-4" />
                  {t('page.home.connections.introduceTo')}
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded p-2 text-left text-primary hover:bg-secondary-50"
                  onClick={() => handleOtherOption('share')}>
                  <FiShare2 className="h-4 w-4" />
                  {t('page.home.connections.share')}
                </button>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded p-2 text-left text-error hover:bg-secondary-50"
                  onClick={() => handleOtherOption('break')}>
                  <FiTrash2 className="h-4 w-4 text-error" />
                  {t('page.home.connections.breakConnection')}
                </button>
              </div>
            )}
          </div>
        </div>

        <ConnectionProfileFields
          fields={profileFields}
          loading={loadingFields}
          className="mx-auto mt-6 w-full md:w-1/2"
        />

        {/* Modals for other options */}
        <IntroductionModal
          open={showIntroModal}
          setOpen={setShowIntroModal}
          userId={connection.id}
          alias={`${connection.firstName}+${connection.lastName}`}
        />
        <ShareWithConnectionModal
          open={showShareModal}
          setOpen={setShowShareModal}
          connectionId={connection.id}
          connectionName={`${connection.firstName}+${connection.lastName}`}
        />
        <WarningModal
          open={showBreakConfirm}
          setOpen={setShowBreakConfirm}
          title={t('warning.deleteConnection.title')}
          description={t('warning.deleteConnection.description')}
          buttonText={t('warning.deleteConnection.button')}
          onSubmit={handleBreakConnection}
        />
      </div>
    </div>
  );
}
